/**
 * GitHub Auth Store
 *
 * Zustand store for GitHub OAuth state.
 * Uses httpOnly cookies managed by the backend — the store only
 * tracks the user object returned from status/login calls.
 */

import { create } from 'zustand';
import {
  checkGitHubAuthStatus,
  openGitHubLogin,
  logoutGitHub,
} from '@/services/githubAuthService';
import type { GitHubUser } from '@/services/githubAuthService';
import { useAppStore } from '@/stores/appStore';

export type { GitHubUser };

interface GitHubAuthState {
  isAuthenticated: boolean;
  user: GitHubUser | null;
  isLoading: boolean;
  error: string | null;
  checkStatus: (getOttoToken: () => Promise<string | null>) => Promise<void>;
  login: () => Promise<void>;
  logout: (getOttoToken: () => Promise<string | null>) => Promise<void>;
  setUser: (user: GitHubUser) => void;
  clear: () => void;
}

export const useGitHubAuthStore = create<GitHubAuthState>()((set, get) => ({
  isAuthenticated: false,
  user: null,
  isLoading: false,
  error: null,

  checkStatus: async (getOttoToken: () => Promise<string | null>) => {
    // Skip if login is already in progress to avoid overwriting result
    if (get().isLoading) return;
    const projectName = useAppStore.getState().selectedProject ?? '';
    if (!projectName.trim()) {
      set({ isAuthenticated: false, user: null, isLoading: false, error: null });
      return;
    }

    set({ isLoading: true });
    try {
      const accessToken = await getOttoToken();
      if (!accessToken) {
        set({ isAuthenticated: false, user: null, isLoading: false, error: null });
        return;
      }

      const { authenticated, user } = await checkGitHubAuthStatus(projectName, accessToken);
      set({ isAuthenticated: authenticated, user: user ?? null, isLoading: false });
    } catch {
      set({ isAuthenticated: false, user: null, isLoading: false });
    }
  },

  login: async () => {
    // Guard against double-click / concurrent login attempts
    if (get().isLoading) return;
    set({ isLoading: true, error: null });
    try {
      const user = await openGitHubLogin();
      set({ isAuthenticated: true, user, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'GitHub login failed';
      // "popup was closed" is user-initiated, not an error
      if (message.includes('popup was closed')) {
        set({ isLoading: false });
      } else {
        set({ isLoading: false, error: message });
        console.error('[GitHubAuth] Login failed:', err);
      }
    }
  },

  logout: async (getOttoToken: () => Promise<string | null>) => {
    const projectName = useAppStore.getState().selectedProject ?? '';
    set({ isLoading: true, error: null });
    try {
      const accessToken = await getOttoToken();
      if (accessToken) {
        await logoutGitHub(projectName, accessToken);
      }
    } catch (err) {
      console.error('[GitHubAuth] Logout failed:', err);
    }
    set({ isAuthenticated: false, user: null, isLoading: false });
  },

  setUser: (user: GitHubUser) => {
    set({ isAuthenticated: true, user, error: null });
  },

  clear: () => {
    set({ isAuthenticated: false, user: null, isLoading: false, error: null });
  },
}));
